import Cookies from 'js-cookie';
import {
  ChartBar,
  DollarSign,
  ExternalLink,
  Loader
} from "lucide-react";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

export default function HomePage() {
  const sessionId = Cookies.get('sessionId');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const userCookie = Cookies.get('user');
  let userData = null;
  try {
    userData = userCookie ? JSON.parse(userCookie) : null;
  } catch (error) {
    console.error("Error parsing user cookie:", error);
    userData = null;
  }
  const name = Array.isArray(userData) ? userData[0]?.name : userData?.name;
  const licence_id = searchParams.get("licence_id");
  const licenceId = localStorage.getItem("licence_id");

  useEffect(() => {
    if (licence_id) {
      if (licence_id !== licenceId) {
        localStorage.setItem("licence_id", licence_id);
      }
      void navigate(".", { replace: true });
      return;
    }
    if (!licenceId && !licence_id) {
      void navigate("?licence_id=", { replace: true });
    }
  }, [licence_id, licenceId, navigate]);

  const menuButtons = [
    { label: 'View monthly invoices', path: '/invoices', Icon: DollarSign, isExternal: false },
    { label: 'View item sales', path: '/item_sales', Icon: ChartBar, isExternal: false },
    { label: 'Itqan Online', path: 'https://itqan.ntelgnt.com/', Icon: ExternalLink, isExternal: true },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="px-4 min-h-[calc(100vh)] flex flex-col items-center justify-center">
        <div className="max-w-screen-xl mx-auto w-full">

          <header className="px-6 py-3 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-200"></div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{t("welcome")}, {name} </h1>
              </div>
            </div>
            <Link
              to='https://itqan.ntelgnt.com/'
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 h-10 rounded-xl bg-slate-100/80 text-slate-600 hover:bg-violet-50 hover:text-violet-600 border border-slate-200 transition-all duration-300 shadow-sm"
            >
              <span className="text-xs font-bold hidden sm:inline">
                {("Itqan online")}
              </span>
              <ExternalLink className="w-4 h-4" />
            </Link>
          </header>

          <main>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <section className="relative rounded-[2.5rem] overflow-hidden shadow-xl h-45 lg:h-[450px] group cursor-pointer">
                <img
                  src="https://thumbs.dreamstime.com/b/electronic-invoicing-system-professional-business-accounting-suit-interacting-digital-interface-displaying-425814910.jpg?w=992" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  alt="Warehouse"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end p-8">

                </div>
              </section>

              <div className="space-y-4">
                <section>
                  <h2 className="text-lg font-bold text-gray-800 mb-4">{t("Quick Access")}</h2>
                  <div className="flex lg:grid lg:grid-cols-4 justify-between pb-2">
                    {menuButtons.map((btn, index) => (
                      <Link
                        key={index}
                        to={btn.path}
                        className="flex flex-col items-center gap-2 min-w-[80px]"
                      >
                        <div className="w-16 h-16 bg-[#333333] rounded-2xl flex items-center justify-center text-white hover:bg-violet-600 transition-all shadow-lg transform hover:-translate-y-1">
                          <btn.Icon size={28} />
                        </div>
                        <span className="text-sm font-bold text-gray-600 text-center">{t(btn.label)}</span>
                      </Link>
                    ))}
                  </div>
                </section>
                <section className="grid grid-cols-2 gap-4 pb-1">
                  <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-50">
                    <h3 className="text-xs font-bold text-gray-500 text-center mb-4">التقرير الأسبوعي</h3>
                    <div className="flex items-end justify-between md:h-40 h-24 gap-1 px-1">
                      {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="group relative flex flex-col items-center flex-1 h-full justify-end">
                          <div
                            style={{ height: `${(i + 1) * 10}%` }}
                            className={`w-full max-w-[10px] rounded-full transition-all duration-500 ease-out bg-violet-500 shadow-[0_0_10px_rgba(139,92,246,0.3)]`}
                          ></div>
                          <span className="text-[8px] text-gray-400 mt-2">Day</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <CircularProgress
                    label="invoices"
                    loading={false}
                    percentage={75}
                  />

                </section>

                <div className="text-gray-400 text-[11px] text-center lg:text-right border-t border-gray-100 pt-2">
                  Session: <span className="text-gray-600 font-medium">{sessionId}</span> |
                  Licence: <span className="text-gray-600 font-medium">{licenceId}</span>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}


const CircularProgress = ({ percentage, loading, label }: { percentage: number, loading: boolean, label: string }) => {
  return (
    <div className="bg-white px-6 py-1 rounded-3xl shadow-sm border border-gray-50 flex flex-col items-center justify-center">
      <h3 className="text-xs font-bold text-gray-500 mb-2">{label}</h3>
      <div className="relative w-20 h-20">
        {loading ? (
          <div className='flex items-center justify-center h-full animate-spin'>
            <Loader className="text-violet-500" />
          </div>
        ) : (
          <>
            <svg className="w-full h-full" viewBox="0 0 36 36">
              <path className="text-gray-100" strokeDasharray="100, 100" strokeWidth="3" fill="none" stroke="currentColor" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              <path className="text-violet-500" strokeDasharray={`${percentage}, 100`} strokeWidth="3" strokeLinecap="round" fill="none" stroke="currentColor" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-black">{percentage}%</span>
          </>
        )}
      </div>
    </div>
  );
};